import { useParams, Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useLocation } from '@/hooks/useLocations';
import { useJobs } from '@/hooks/useJobs';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { AddressPhotoCard } from './AddressPhotoCard';
import { formatClientName, formatManagerName } from '@jobos/shared';

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: location, isLoading: locationLoading, error: locationError } = useLocation(id);
  const { data: jobs, isLoading: jobsLoading } = useJobs(id ? { locationId: [id] } : undefined);

  if (locationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (locationError || !location) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">
          Error loading service address: {locationError?.message || 'Service address not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          Service Address
        </h1>
      </div>

      {/* Location Info Card */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Address</h3>
              {location.name && (
                <p className="text-lg font-semibold text-gray-900">{location.name}</p>
              )}
              <p className="text-gray-900">
                {location.addressLine1}
                {location.addressLine2 && `, ${location.addressLine2}`}
              </p>
              <p className="text-gray-900">
                {location.city}
                {location.state && `, ${location.state}`}
                {location.zip && ` ${location.zip}`}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Client</h3>
              {location.client ? (
                <Link
                  to={`/clients/${location.clientId}`}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {formatClientName(location.client.name, location.client.firstName, location.client.lastName)}
                </Link>
              ) : (
                <p className="text-gray-900">-</p>
              )}

              {location.manager && (
                <>
                  <h3 className="text-sm font-medium text-gray-500 mt-4 mb-1">Manager</h3>
                  <p className="text-gray-900">{formatManagerName(location.manager.name, location.manager.firstName, location.manager.lastName)}</p>
                  {location.manager.email && (
                    <p className="text-sm text-gray-600">{location.manager.email}</p>
                  )}
                  {location.manager.phone && (
                    <p className="text-sm text-gray-600">{location.manager.phone}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Address Photo */}
      <AddressPhotoCard locationId={location.id} photoUrl={location.photoUrl} />

      {/* Jobs Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Jobs at this Address ({jobs?.length || 0})
          </h2>

          {jobsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
            </div>
          ) : jobs && jobs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job Code</TableHead>
                  <TableHead>Project Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">
                      {job.jobCode}
                    </TableCell>
                    <TableCell>
                      {job.projectType === 'Other' && job.customProjectType
                        ? job.customProjectType
                        : job.projectType}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {job.description || '-'}
                    </TableCell>
                    <TableCell>
                      <JobStatusBadge status={job.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/jobs/${job.id}`}>
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No jobs at this address</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
